import { stripMarkdown } from "@/components/molecules/markdown";
import { UnsureNameMark } from "@/components/molecules/unsure-name-mark";
import { Badge } from "@/components/ui/badge";
import {
  deadlineWasPromised,
  messageIsLate,
  type ClientMessage,
} from "@/features/messages/types";
import type { DateFormat } from "@/features/utils/dates";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";
import { ClockIcon, PhoneCallIcon } from "@phosphor-icons/react";
import type { ColumnDef } from "@tanstack/react-table";
import { Action } from "./columns-action";

/**
 * Day and hour, never seconds: this list is read to decide what to handle next, and
 * "22/09/2026 07:22:29" spends a whole column saying the same thing twice.
 */
const formatDate = (format: DateFormat["format"], timestamp?: number) =>
  timestamp ? format(timestamp, "Pp") : "-";

export function useMessageColumns(options?: {
  /** Drop the customer column — on a contact's own file it names the same person on every row. */
  hideCustomer?: boolean;
}): ColumnDef<ClientMessage>[] {
  const { t } = useTranslation("messages");
  const { format } = useDateFormat();

  const columns: ColumnDef<ClientMessage>[] = [
    {
      accessorKey: "subject",
      header: t("table.subject"),
      cell: ({ row }) => {
        const message = row.original;
        return (
          <div className="max-w-md space-y-1">
            <div className="flex items-center gap-2 font-medium">
              <span className="truncate">{message.subject}</span>
              {message.urgency === "high" && (
                <Badge
                  variant="outline"
                  className="shrink-0 border-red-500/40 text-red-600"
                >
                  {t("table.urgent")}
                </Badge>
              )}
              {messageIsLate(message) && (
                <Badge
                  variant="outline"
                  className="shrink-0 border-amber-500/40 text-amber-600"
                >
                  <ClockIcon className="mr-1 h-3 w-3" weight="fill" />
                  {t("table.late")}
                </Badge>
              )}
            </div>
            <div className="text-muted-foreground line-clamp-2 text-sm">
              {stripMarkdown(message.body)}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "customer_name",
      header: t("table.customer"),
      cell: ({ row }) => (
        <div className="space-y-0.5">
          <div className="text-sm">
            {row.original.customer_name || t("table.unknownCustomer")}
            {row.original.customer_name &&
              row.original.unsure_customer_name && <UnsureNameMark />}
          </div>
          {row.original.phone_number && (
            // A number split across two lines is a number nobody can read back.
            <div className="text-muted-foreground text-xs whitespace-nowrap">
              {row.original.phone_number}
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: "assigned_member_name",
      header: t("table.assignedTo"),
      cell: ({ row }) =>
        row.original.assigned_member_name ? (
          <Badge variant="secondary" className="whitespace-nowrap">
            {row.original.assigned_member_name}
          </Badge>
        ) : (
          <span className="text-muted-foreground">{t("table.anyone")}</span>
        ),
    },
    {
      accessorKey: "created_at",
      header: t("table.received"),
      cell: ({ row }) => (
        <div className="space-y-0.5">
          <div className="text-muted-foreground text-sm">
            {formatDate(format, row.original.created_at)}
          </div>
          {!!row.original.due_at && (
            <div className="text-muted-foreground text-xs">
              {t(
                deadlineWasPromised(row.original)
                  ? "table.promisedFor"
                  : "table.deadlineOn",
                { date: formatDate(format, row.original.due_at) },
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: t("table.status"),
      cell: ({ row }) => {
        const message = row.original;
        // An answer written on a request nobody closed is worth its own state: the
        // customer already has what they asked for, the team still owes a decision.
        const label =
          message.status === "pending"
            ? message.reply
              ? "answered"
              : "pending"
            : message.resolution || "dropped";

        return (
          <div className="space-y-1">
            <Badge
              variant={message.status === "pending" ? "outline" : "secondary"}
              className="whitespace-nowrap"
            >
              {message.resolution === "delegated" && (
                <PhoneCallIcon className="mr-1 h-3 w-3" weight="fill" />
              )}
              {t(`status.${label}`)}
            </Badge>
            {!!message.reply && (
              <div className="text-muted-foreground line-clamp-2 max-w-xs text-xs">
                {message.reply}
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: "actions",
      header: () => <div className="text-right">{t("table.actions")}</div>,
      cell: ({ row }) => <Action message={row.original} />,
    },
  ];

  return options?.hideCustomer
    ? columns.filter(
        (column) =>
          !("accessorKey" in column) || column.accessorKey !== "customer_name",
      )
    : columns;
}
